import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { LifeBuoy, Loader2, RefreshCw, Mail, ChevronDown, ChevronUp, CheckCircle2, Clock, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { supportAPI } from '../lib/api';
import { cn } from '../lib/utils';

const STATUS_LABELS = {
    abierto: { label: 'Abierto', color: 'bg-rose-100 text-rose-700', icon: Inbox },
    en_proceso: { label: 'En proceso', color: 'bg-amber-100 text-amber-700', icon: Clock },
    cerrado: { label: 'Cerrado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

function TicketRow({ ticket, onUpdate }) {
    const [expanded, setExpanded] = useState(false);
    const [nota, setNota] = useState(ticket.nota_admin || '');
    const [busy, setBusy] = useState(false);
    const statusMeta = STATUS_LABELS[ticket.status] || STATUS_LABELS.abierto;
    const StatusIcon = statusMeta.icon;

    const updateStatus = async (status) => {
        setBusy(true);
        try {
            await supportAPI.updateStatus(ticket.id, { status, nota: nota || null });
            toast.success(`Ticket marcado como "${STATUS_LABELS[status].label}"`);
            onUpdate();
        } catch {
            toast.error('No se pudo actualizar el ticket');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className={cn(
                'rounded-xl border bg-white transition-shadow',
                ticket.status === 'abierto' ? 'border-rose-200 shadow-sm' : 'border-slate-200'
            )}
            data-testid={`support-ticket-${ticket.id}`}
        >
            {/* Cabecera */}
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left"
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={cn('text-[10px]', statusMeta.color)}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusMeta.label}
                        </Badge>
                        <span className="font-medium text-sm text-slate-900 truncate">
                            {ticket.asunto || 'Consulta de soporte'}
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 truncate">
                        <strong className="text-slate-700">{ticket.nombre || ticket.email}</strong> · {ticket.email} ·{' '}
                        {new Date(ticket.created_at).toLocaleString('es-CL', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </p>
                </div>
                {expanded ? (
                    <ChevronUp className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
                ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 mt-1" />
                )}
            </button>

            {/* Contenido expandido */}
            {expanded && (
                <div className="border-t border-slate-100 px-4 py-4 space-y-4">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            Mensaje del docente
                        </p>
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                            {ticket.mensaje}
                        </div>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2 text-[11px] text-slate-500">
                        {ticket.url && (
                            <div>
                                <span className="font-semibold text-slate-600">URL: </span>
                                <span className="break-all">{ticket.url}</span>
                            </div>
                        )}
                        {ticket.estrategia_activa && (
                            <div>
                                <span className="font-semibold text-slate-600">Estrategia: </span>
                                {ticket.estrategia_activa}
                            </div>
                        )}
                        {ticket.user_agent && (
                            <div className="sm:col-span-2 truncate">
                                <span className="font-semibold text-slate-600">User-Agent: </span>
                                {ticket.user_agent}
                            </div>
                        )}
                    </div>

                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            Nota interna (opcional)
                        </p>
                        <Textarea
                            value={nota}
                            onChange={(e) => setNota(e.target.value)}
                            placeholder="Notas para el equipo (no se envían al usuario)…"
                            rows={2}
                            maxLength={2000}
                            className="text-sm resize-none"
                            data-testid={`support-ticket-note-${ticket.id}`}
                        />
                    </div>

                    {/* Acciones */}
                    <div className="flex flex-wrap items-center gap-2">
                        <a
                            href={`mailto:${ticket.email}?subject=Re:%20${encodeURIComponent(ticket.asunto || 'Consulta de soporte')}`}
                            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                            data-testid={`support-ticket-reply-${ticket.id}`}
                        >
                            <Mail className="h-3 w-3" />
                            Responder por correo
                        </a>
                        {ticket.status !== 'en_proceso' && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => updateStatus('en_proceso')}
                                className="text-xs"
                                data-testid={`support-ticket-progress-${ticket.id}`}
                            >
                                Marcar en proceso
                            </Button>
                        )}
                        {ticket.status !== 'cerrado' && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => updateStatus('cerrado')}
                                className="text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                data-testid={`support-ticket-close-${ticket.id}`}
                            >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Cerrar ticket
                            </Button>
                        )}
                        {ticket.status === 'cerrado' && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => updateStatus('abierto')}
                                className="text-xs"
                                data-testid={`support-ticket-reopen-${ticket.id}`}
                            >
                                Reabrir
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function SupportTicketsPanel() {
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [counts, setCounts] = useState({ abierto: 0, en_proceso: 0, cerrado: 0 });
    const [filter, setFilter] = useState('all');

    const fetchTickets = useCallback(async () => {
        try {
            const params = filter === 'all' ? {} : { status: filter };
            const { data } = await supportAPI.listTickets(params);
            setTickets(data.tickets || []);
            setCounts(data.counts || { abierto: 0, en_proceso: 0, cerrado: 0 });
        } catch {
            toast.error('No se pudieron cargar los tickets');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        setLoading(true);
        fetchTickets();
    }, [fetchTickets]);

    return (
        <Card data-testid="support-tickets-panel" id="soporte">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <LifeBuoy className="h-5 w-5" />
                            Bandeja de Soporte
                            {counts.abierto > 0 && (
                                <Badge className="bg-rose-500 text-white text-[10px]">
                                    {counts.abierto} abierto{counts.abierto === 1 ? '' : 's'}
                                </Badge>
                            )}
                        </CardTitle>
                        <CardDescription>
                            Tickets enviados por docentes desde el widget flotante. Notificaciones por correo a{' '}
                            <strong>acontistaeduca@gmail.com</strong>.
                        </CardDescription>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={fetchTickets}
                        disabled={loading}
                        className="gap-1.5"
                        data-testid="support-refresh-btn"
                    >
                        <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                        Refrescar
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Filtros / contadores */}
                <div className="flex flex-wrap gap-2">
                    {[
                        { key: 'all', label: 'Todos', count: counts.abierto + counts.en_proceso + counts.cerrado },
                        { key: 'abierto', label: 'Abiertos', count: counts.abierto },
                        { key: 'en_proceso', label: 'En proceso', count: counts.en_proceso },
                        { key: 'cerrado', label: 'Cerrados', count: counts.cerrado },
                    ].map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFilter(f.key)}
                            data-testid={`support-filter-${f.key}`}
                            className={cn(
                                'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                                filter === f.key
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            )}
                        >
                            {f.label} <span className="font-mono text-[10px] opacity-80">({f.count})</span>
                        </button>
                    ))}
                </div>

                {/* Listado */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
                        <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p className="font-medium">Sin tickets en esta vista</p>
                        <p className="text-xs mt-1">Cuando un docente abra una consulta aparecerá aquí.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {tickets.map((t) => (
                            <TicketRow key={t.id} ticket={t} onUpdate={fetchTickets} />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
